import typia from 'typia';
import { dispatchAction } from '@ardoq/rxbeach';
import { ViewIds } from '@ardoq/api-types';
import type { LoadedGraphWithViewpointMode } from '@ardoq/data-model';
import { getComponentTypesForLegend } from '@ardoq/view-legend';
import { updateViewSettings } from '@ardoq/view-settings';
import { type AssistantDiagramRecipePayload, parseRecipe, } from 'tabview/assistantDiagram/recipe';
import { setAssistantDiagramRecipe } from 'tabview/assistantDiagram/assistantDiagram$';
import { AiControlledCommandId } from 'aiControlledCommands/aiControlledCommandId';
import { defineAiControlledCommand } from 'aiControlledCommands/defineAiControlledCommand';
import { VIEWPOINT_DOMAIN } from 'aiControlledCommands/viewpoint/domain';
import { WHEN_TO_USE, DIAGRAM_CONTEXT_DESCRIPTION } from './descriptions';
import type { AssistantDiagramRecipe } from 'tabview/assistantDiagram/recipe';
import { isEqual } from 'lodash';
import type { AiControlledCommand } from 'aiControlledCommands/types';
type DiagramContext = {
    componentTypes: string[];
    currentRecipe: AssistantDiagramRecipe | null;
};
const buildDiagramContext = (loadedGraph: LoadedGraphWithViewpointMode, currentRecipe: AssistantDiagramRecipe | null): DiagramContext => ({
    componentTypes: getComponentTypesForLegend([
        ...loadedGraph.scopeData.scopeComponentIds,
    ]).map(({ label }) => label),
    currentRecipe,
});
export const buildDiagramCommand = (loadedGraph: LoadedGraphWithViewpointMode, currentRecipe: AssistantDiagramRecipe | null): AiControlledCommand<typeof diagramCommandDefinition> => ({
    ...diagramCommandDefinition,
    context: buildDiagramContext(loadedGraph, currentRecipe),
});
export const executeGenerateDiagram = (recipe: AssistantDiagramRecipe): void => {
    dispatchAction(setAssistantDiagramRecipe(recipe));
    // Seed legend visibility to the recipe's choice, non-persistently so it doesn't
    // clobber the user's saved default; they can still toggle it from the settings bar.
    dispatchAction(updateViewSettings({
        viewId: ViewIds.ASSISTANT_DIAGRAM,
        settings: { isLegendActive: recipe.showLegend },
        persistent: false,
    }));
};
export const diagramCommandDefinition = defineAiControlledCommand<AssistantDiagramRecipe, DiagramContext>({
    name: AiControlledCommandId.GENERATE_DIAGRAM,
    requiresAppShell: true,
    domain: VIEWPOINT_DOMAIN,
    description: WHEN_TO_USE,
    parameters: {
        version: "3.1",
        components: {
            schemas: {
                AssistantDiagramRecipe: {
                    type: "object",
                    properties: {
                        title: {
                            type: "string",
                            description: "A short, descriptive title for the diagram."
                        },
                        layout: {
                            $ref: "#/components/schemas/RecipeLayout",
                            description: "The layout and the options that layout supports. Choose the layout that best\nfits the data, then set its options: 'hierarchical' (set orientation +\nedgeRouting, and groupEdges to bus-group connections); 'organic'; 'circular'\n(set bundleEdges on dense graphs); 'orthogonal'; 'tree'; 'radial' (set\nbundleEdges on dense graphs); 'compact-disk'."
                        },
                        showLegend: {
                            type: "boolean",
                            description: "Whether to show a legend mapping colors/shapes to component types."
                        }
                    },
                    required: [
                        "title",
                        "layout",
                        "showLegend"
                    ],
                    description: "A small declarative recipe describing how to lay out and style the graph the\nuser is currently viewing in the Assistant Diagram. This is NOT geometry: it\ncarries no node positions or coordinates. The frontend already holds the\nin-view graph and lays it out client-side (yFiles) by applying this recipe, so\nthe output stays tiny and fast regardless of graph size. When the request is\nvague, default to a hierarchical layout with the legend on."
                },
                RecipeLayout: {
                    oneOf: [
                        {
                            type: "object",
                            properties: {
                                type: {
                                    "const": "hierarchical",
                                    description: "Layered layout for dependency/flow/breakdown structures with a sense of direction."
                                },
                                orientation: {
                                    $ref: "#/components/schemas/RecipeOrientation",
                                    description: "Flow direction of the layered layout; pick what reads best."
                                },
                                edgeRouting: {
                                    $ref: "#/components/schemas/HierarchicalLayoutRoutingStyleStringValues",
                                    description: "How edges are routed: 'orthogonal' for right-angled connectors,\n'octilinear' for 45-degree diagonal segments, 'polyline' for direct\nstraight lines, 'curved' for smooth curves."
                                },
                                groupEdges: {
                                    type: "boolean",
                                    description: "Group edges sharing a source or target into a common bus, reducing the\nnumber of distinct edge entry/exit points per node. Keep on for most\ngraphs; turn off when individual edge paths must stay distinct."
                                }
                            },
                            required: [
                                "type",
                                "orientation",
                                "edgeRouting",
                                "groupEdges"
                            ]
                        },
                        {
                            type: "object",
                            properties: {
                                type: {
                                    "const": "organic",
                                    description: "Force-directed layout for densely interconnected graphs with no clear direction."
                                }
                            },
                            required: [
                                "type"
                            ]
                        },
                        {
                            type: "object",
                            properties: {
                                type: {
                                    "const": "circular",
                                    description: "Arranges nodes around circles \u2014 good for showing clusters and cycles."
                                },
                                bundleEdges: {
                                    type: "boolean",
                                    description: "Bundle edges sharing a circle partition into tighter strands to cut clutter on dense graphs."
                                }
                            },
                            required: [
                                "type",
                                "bundleEdges"
                            ]
                        },
                        {
                            type: "object",
                            properties: {
                                type: {
                                    "const": "orthogonal",
                                    description: "Schematic, grid-routed layout for network-diagram style structures."
                                }
                            },
                            required: [
                                "type"
                            ]
                        },
                        {
                            type: "object",
                            properties: {
                                type: {
                                    "const": "tree",
                                    description: "Strict hierarchy / breakdown-structure layout."
                                }
                            },
                            required: [
                                "type"
                            ]
                        },
                        {
                            type: "object",
                            properties: {
                                type: {
                                    "const": "radial",
                                    description: "Arranges nodes on concentric circles around central nodes (distance from centre = depth)."
                                },
                                bundleEdges: {
                                    type: "boolean",
                                    description: "Bundle edges toward the centre into tighter strands to cut clutter on dense graphs."
                                }
                            },
                            required: [
                                "type",
                                "bundleEdges"
                            ]
                        },
                        {
                            type: "object",
                            properties: {
                                type: {
                                    "const": "compact-disk",
                                    description: "Packs nodes tightly into a disk \u2014 good for large sets with few edges."
                                }
                            },
                            required: [
                                "type"
                            ]
                        }
                    ],
                    description: "The layout choice plus only the options that layout actually supports \u2014 a tagged\nunion keyed by `type`. This keeps layout-specific options off layouts that ignore\nthem, and lets the contract grow (per-layout options) without a flat bag of\nconditionally-meaningful fields."
                },
                RecipeOrientation: {
                    oneOf: [
                        {
                            "const": "left-to-right"
                        },
                        {
                            "const": "top-to-bottom"
                        }
                    ]
                },
                HierarchicalLayoutRoutingStyleStringValues: {
                    oneOf: [
                        {
                            "const": "orthogonal"
                        },
                        {
                            "const": "octilinear"
                        },
                        {
                            "const": "polyline"
                        },
                        {
                            "const": "curved"
                        }
                    ],
                    description: "Automatic conversion to {@link HierarchicalLayoutRoutingStyle}."
                }
            }
        },
        schema: {
            $ref: "#/components/schemas/AssistantDiagramRecipe"
        }
    } as import("typia").IJsonSchemaUnit<"3.1">,
    contextDescription: DIAGRAM_CONTEXT_DESCRIPTION,
    isContext: (context): context is DiagramContext => (() => { const _io0 = (input: any): boolean => Array.isArray(input.componentTypes) && input.componentTypes.every((elem: any) => "string" === typeof elem) && (null === input.currentRecipe || "object" === typeof input.currentRecipe && null !== input.currentRecipe && _io1(input.currentRecipe)); const _io1 = (input: any): boolean => "string" === typeof input.title && ("object" === typeof input.layout && null !== input.layout && _iu0(input.layout)) && "boolean" === typeof input.showLegend; const _io2 = (input: any): boolean => "hierarchical" === input.type && ("left-to-right" === input.orientation || "top-to-bottom" === input.orientation) && ("orthogonal" === input.edgeRouting || "octilinear" === input.edgeRouting || "polyline" === input.edgeRouting || "curved" === input.edgeRouting) && "boolean" === typeof input.groupEdges; const _io3 = (input: any): boolean => "organic" === input.type; const _io4 = (input: any): boolean => "circular" === input.type && "boolean" === typeof input.bundleEdges; const _io5 = (input: any): boolean => "orthogonal" === input.type; const _io6 = (input: any): boolean => "tree" === input.type; const _io7 = (input: any): boolean => "radial" === input.type && "boolean" === typeof input.bundleEdges; const _io8 = (input: any): boolean => "compact-disk" === input.type; const _iu0 = (input: any): any => (() => {
        if ("hierarchical" === input.type)
            return _io2(input);
        else if ("organic" === input.type)
            return _io3(input);
        else if ("radial" === input.type)
            return _io7(input);
        else if ("circular" === input.type)
            return _io4(input);
        else if ("compact-disk" === input.type)
            return _io8(input);
        else if ("tree" === input.type)
            return _io6(input);
        else if ("orthogonal" === input.type)
            return _io5(input);
        else
            return false;
    })(); return (input: any): input is DiagramContext => "object" === typeof input && null !== input && _io0(input); })()(context),
    validate: rawPayload => {
        if (!(() => { const _io0 = (input: any): boolean => "string" === typeof input.title && ("object" === typeof input.layout && null !== input.layout && _iu0(input.layout)) && "boolean" === typeof input.showLegend; const _io1 = (input: any): boolean => "hierarchical" === input.type && ("left-to-right" === input.orientation || "top-to-bottom" === input.orientation) && ("orthogonal" === input.edgeRouting || "octilinear" === input.edgeRouting || "polyline" === input.edgeRouting || "curved" === input.edgeRouting) && "boolean" === typeof input.groupEdges; const _io2 = (input: any): boolean => "organic" === input.type; const _io3 = (input: any): boolean => "circular" === input.type && "boolean" === typeof input.bundleEdges; const _io4 = (input: any): boolean => "orthogonal" === input.type; const _io5 = (input: any): boolean => "tree" === input.type; const _io6 = (input: any): boolean => "radial" === input.type && "boolean" === typeof input.bundleEdges; const _io7 = (input: any): boolean => "compact-disk" === input.type; const _iu0 = (input: any): any => (() => {
            if ("hierarchical" === input.type)
                return _io1(input);
            else if ("organic" === input.type)
                return _io2(input);
            else if ("radial" === input.type)
                return _io6(input);
            else if ("circular" === input.type)
                return _io3(input);
            else if ("compact-disk" === input.type)
                return _io7(input);
            else if ("tree" === input.type)
                return _io5(input);
            else if ("orthogonal" === input.type)
                return _io4(input);
            else
                return false;
        })(); return (input: any): input is AssistantDiagramRecipePayload => "object" === typeof input && null !== input && _io0(input); })()(rawPayload)) {
            return {
                status: 'rejected',
                reason: 'The AI returned an invalid diagram layout.',
            };
        }
        const recipe = parseRecipe(rawPayload);
        return recipe
            ? { status: 'valid', payload: recipe }
            : {
                status: 'rejected',
                reason: 'The AI returned an invalid diagram layout.',
            };
    },
    execute: executeGenerateDiagram,
    isApplied: (recipe, context) => isEqual(recipe, context.currentRecipe),
});
